import { Modal } from "antd";
import type { ReactNode } from "react";
import { Trans } from "react-i18next";
import { CommonButton } from "@components/CommonButton";
import { IconClose, IconErrorRed, IconInfo } from "@icons/index";
import type { UserPublishResultResponse } from "@apis/http/api";

type PublishIssue = NonNullable<UserPublishResultResponse["issues"]>[number];

/**
 * TODO(leon): 与后端对齐完整列表，目前覆盖 access_token 失效与 shop authorization 过期。二期实现，本期先不实现过期逻辑
 */
export const AUTH_FAIL_CODES = new Set<string>([
  // "TIKTOK-105003",
  // "TIKTOK-105004",
  // "TIKTOK-105005",
  // "TIKTOK-36004003",
  // "TIKTOK-36004004",
]);

export const isAuthFailCode = (failCode?: string | null) => !!failCode && AUTH_FAIL_CODES.has(failCode);

/**
 * 预检通过但有质量 issues，等待用户二次确认。
 * 对应 status === "PRE_CHECK_SUCCESS" && needConfirm === true
 */
export interface OptimizationSuggestionsModalProps {
  open: boolean;
  issues: PublishIssue[];
  confirming?: boolean;
  onClose: () => void;
  onContinue: () => void;
}

export const OptimizationSuggestionsModal = (props: OptimizationSuggestionsModalProps) => {
  const { open, issues, confirming, onClose, onContinue } = props;
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={440}
      centered
      destroyOnHidden
      mask={{ closable: false }}
      closable={false}
      styles={{ body: { padding: 0 }, container: { padding: 0 } }}
    >
      <div className="flex flex-col rounded-[12px] bg-kds-bg">
        <div className="flex h-[64px] items-center gap-[8px] px-[16px]">
          <IconInfo className="size-[20px] shrink-0 text-kds-accent-fg" />
          <span className="flex-1 text-[16px] font-semibold leading-[24px] text-kds-fg">
            <Trans>Optimization suggestions</Trans>
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={confirming}
            className="flex size-[32px] shrink-0 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent text-kds-fg-soft hover:bg-kds-bg-overlay disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close"
          >
            <IconClose className="size-[20px]" />
          </button>
        </div>

        <div className="flex flex-col gap-[8px] px-[16px] pb-[16px] text-[16px] leading-[24px] text-kds-fg">
          <p>
            <Trans>Your video may perform better if:</Trans>
          </p>
          <ul className="ml-[24px] list-disc">
            {issues.map((issue, index) => (
              <li key={`${issue.risk ?? "issue"}-${index}`}>
                {issue.risk ? `${issue.risk}: ${issue.suggestions ?? ""}` : issue.suggestions}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex h-[64px] items-center justify-end gap-[8px] border-t border-kds-border px-[16px]">
          <CommonButton type="default" size="middle" disabled={confirming} onClick={onClose}>
            <Trans>Back to Edit</Trans>
          </CommonButton>
          <CommonButton type="primary" size="middle" loading={confirming} onClick={onContinue}>
            <Trans>Continue to Post</Trans>
          </CommonButton>
        </div>
      </div>
    </Modal>
  );
};

/**
 * 预检失败终态。
 * 对应 status === "PRE_CHECK_FAIL"
 * - 授权类失败：显示 Reconnect Account 按钮
 * - 其它失败：显示 Back to Edit 按钮
 */
export interface PreCheckFailModalProps {
  open: boolean;
  failCode?: string;
  failReason?: string;
  issues?: PublishIssue[];
  reconnecting?: boolean;
  onClose: () => void;
  onReconnect: () => void;
}

export const PreCheckFailModal = (props: PreCheckFailModalProps) => {
  const { open, failCode, failReason, issues, reconnecting, onClose, onReconnect } = props;
  const authIssue = isAuthFailCode(failCode);
  const hasIssues = !!issues && issues.length > 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={440}
      centered
      destroyOnHidden
      mask={{ closable: false }}
      closable={false}
      styles={{ body: { padding: 0 }, container: { padding: 0 } }}
    >
      <div className="flex flex-col rounded-[12px] bg-kds-bg">
        <div className="flex h-[64px] items-center gap-[8px] px-[16px]">
          <IconErrorRed className="size-[20px] shrink-0 text-kds-danger-fg" />
          <span className="flex-1 text-[16px] font-semibold leading-[24px] text-kds-fg">
            <Trans>This video can't be posted</Trans>
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={reconnecting}
            className="flex size-[32px] shrink-0 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent text-kds-fg-soft hover:bg-kds-bg-overlay disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close"
          >
            <IconClose className="size-[20px]" />
          </button>
        </div>

        <div className="flex flex-col gap-[8px] px-[16px] pb-[16px] text-[14px] leading-[22px] text-kds-fg">
          {hasIssues ? (
            <ul className="ml-[24px] list-disc">
              {issues.map((issue, index) => (
                <li key={`${issue.risk ?? "issue"}-${index}`}>
                  {issue.risk ? `${issue.risk}: ${issue.suggestions ?? ""}` : issue.suggestions}
                </li>
              ))}
            </ul>
          ) : (
            <Trans>{failReason || "Pre-check failed. Please revise your content and try again."}</Trans>
          )}
        </div>

        <div className="flex h-[64px] items-center justify-end gap-[8px] border-t border-kds-border px-[16px]">
          {authIssue ? (
            <CommonButton type="primary" size="middle" loading={reconnecting} onClick={onReconnect}>
              <Trans>Reconnect Account</Trans>
            </CommonButton>
          ) : (
            <CommonButton type="primary" size="middle" onClick={onClose}>
              <Trans>Back to Edit</Trans>
            </CommonButton>
          )}
        </div>
      </div>
    </Modal>
  );
};

/**
 * 已发布阶段失败终态（预检通过但发布接口/轮询失败）。
 * 对应 status === "FAIL"
 */
export interface PostFailModalProps {
  open: boolean;
  failReason?: string;
  retryText?: ReactNode;
  retrying?: boolean;
  onClose: () => void;
  onRetry: () => void;
}

export const PostFailModal = (props: PostFailModalProps) => {
  const { open, failReason, retryText, retrying, onClose, onRetry } = props;
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={440}
      centered
      destroyOnHidden
      mask={{ closable: false }}
      closable={false}
      styles={{ body: { padding: 0 }, container: { padding: 0 } }}
    >
      <div className="flex flex-col rounded-[12px] bg-kds-bg">
        <div className="flex h-[64px] items-center gap-[8px] px-[16px]">
          <IconErrorRed className="size-[20px] shrink-0 text-kds-danger-fg" />
          <span className="flex-1 text-[16px] font-semibold leading-[24px] text-kds-fg">
            <Trans>Failed to post content. Please try again.</Trans>
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={retrying}
            className="flex size-[32px] shrink-0 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent text-kds-fg-soft hover:bg-kds-bg-overlay disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close"
          >
            <IconClose className="size-[20px]" />
          </button>
        </div>

        {failReason ? (
          <div className="flex flex-col gap-[8px] px-[16px] pb-[16px] text-[14px] leading-[22px] text-kds-fg-soft">
            <Trans>{failReason}</Trans>
          </div>
        ) : null}

        <div className="flex h-[64px] items-center justify-end gap-[8px] border-t border-kds-border px-[16px]">
          <CommonButton type="primary" size="middle" loading={retrying} onClick={onRetry}>
            {retryText ?? <Trans>Retry posting</Trans>}
          </CommonButton>
        </div>
      </div>
    </Modal>
  );
};
